import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

// FanData (24/09): a data relativa saía em inglês. O `formatDistanceToNow` do date-fns usa o
// locale padrão da biblioteca quando nenhum é passado, e o padrão é inglês, então o painel
// inteiro em português mostrava "less than a minute ago" e "about 21 hours ago" ao lado de
// "Você escreveu". São 66 lugares usando isto, e todos estavam em inglês.
//
// Fixo em pt-BR de propósito: este fork atende um clube brasileiro e a interface não é
// multilíngue na prática. Ler o idioma da conta aqui exigiria carregar o i18n dentro de um
// helper puro, e o ganho seria hipotético.
const IDIOMA = ptBR;

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, { locale: IDIOMA });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat, { locale: IDIOMA });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a', { locale: IDIOMA });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true, locale: IDIOMA });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, { locale: IDIOMA });
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // Encurta a data relativa para o formato das listas: 1m, 2h, 3d, 1mo, 1y.
  //
  // ⚠️ ELA RECEBE TEXTO, não carimbo de tempo, e é sempre a saída do `dynamicTime`. Quando o
  // dynamicTime passou a responder em português (24/09), a versão antiga desta função parou de
  // casar: ela procurava " hours ago" e recebia "há cerca de 2 horas", então devolvia a frase
  // inteira onde a lista esperava "2h". Aconteceria em silêncio, no card de conversa e na caixa
  // de entrada, que é onde mais se olha.
  //
  // Agora ela lê o NÚMERO e a UNIDADE em vez de casar a frase toda, e entende os dois idiomas:
  // há chamadas que passam texto vindo de outros lugares.
  const texto = String(time ?? '');
  const sufixo = withAgo ? ' atrás' : '';

  if (/menos de um minuto|less than a minute/i.test(texto)) return 'agora';

  const unidades = [
    [/minuto|minute/i, 'm'],
    [/hora|hour/i, 'h'],
    [/dia|day/i, 'd'],
    [/m[êe]s|meses|month/i, 'mo'],
    [/ano|year/i, 'y'],
  ];
  const unidade = unidades.find(([padrao]) => padrao.test(texto));
  if (!unidade) return texto;

  // "há cerca de 1 hora" e "an hour ago" não trazem dígito: nos dois casos é 1.
  const numero = texto.match(/\d+/);
  return `${numero ? numero[0] : '1'}${unidade[1]}${sufixo}`;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
